class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  mutationObserver = undefined;
  refreshDebounceTimer = undefined;
  isRefreshing = false;
  autoRefreshDelayMs = 300;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.setupAutoRefreshOnCartDomChange();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = undefined;
    }

    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = undefined;
    }
  }

  setupAutoRefreshOnCartDomChange() {
    // Some upsell widgets (e.g. Recharge) update the cart UI without triggering our normal refresh flow.
    // We detect DOM changes inside the drawer and re-fetch the official cart drawer sections.
    if (!this.drawer) return;

    if (this.mutationObserver) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      if (!this.isOpen) return;
      if (this.isRefreshing) return;
      // Only refresh when the actual cart line items change (e.g. an upsell widget
      // adds/removes a product). Ignore cosmetic injections from third-party app
      // widgets (BeyondLoyalty, rewards, etc.). Without this guard, the loyalty
      // widget injection triggers a full drawer re-render that wipes the widget,
      // the app re-injects it, and we end up in an infinite loop.
      if (!this.hasRelevantCartMutation(mutations)) return;
      this.requestCartDrawerAutoRefresh();
    });

    this.mutationObserver.observe(this.drawer, {
      childList: true,
      subtree: true
      // Avoid observing attributes/characterData to reduce noise.
    });
  }

  hasRelevantCartMutation(mutations) {
    const cartItemSelector = ".wt-cart__item";
    // Selectors for third-party app widgets that inject themselves into the drawer
    // (loyalty / rewards / points). Mutations caused purely by these must be ignored.
    const appWidgetSelector = [
      '[id*="loyalty" i]',
      '[class*="loyalty" i]',
      '[id*="beyond" i]',
      '[class*="beyond" i]',
      '[id*="reward" i]',
      '[class*="reward" i]',
      "[data-beyond-loyalty]",
    ].join(", ");

    const isElement = (node) => node && node.nodeType === 1;

    const isAppWidgetNode = (node) => {
      if (!isElement(node)) return false;
      return (
        (node.matches && node.matches(appWidgetSelector)) ||
        (node.closest && node.closest(appWidgetSelector) != null)
      );
    };

    const isCartItemNode = (node) => {
      if (!isElement(node)) return false;
      if (isAppWidgetNode(node)) return false;
      return (
        (node.matches && node.matches(cartItemSelector)) ||
        (node.querySelector && node.querySelector(cartItemSelector) != null)
      );
    };

    for (const mutation of mutations) {
      // Skip changes happening inside an app widget container.
      if (isAppWidgetNode(mutation.target)) continue;

      const changedNodes = [
        ...Array.from(mutation.addedNodes),
        ...Array.from(mutation.removedNodes),
      ];

      if (changedNodes.some(isCartItemNode)) return true;
    }

    return false;
  }

  requestCartDrawerAutoRefresh() {
    if (this.refreshDebounceTimer) return;

    this.refreshDebounceTimer = setTimeout(() => {
      this.refreshDebounceTimer = undefined;
      if (!this.isOpen) return;
      this.refreshCartDrawer();
    }, this.autoRefreshDelayMs);
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      // this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);

    // dispatch a custom event on the document
    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;

    document.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
      }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e) {
    if (this.isRefreshing) return;
    this.isRefreshing = true;

    const sectionsToRender = this.getSectionsToRender();
    return fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        const parsedState = { sections: response };
        this.renderContents(parsedState, false);
      })
      .catch((fetchErr) => {
        console.log(fetchErr);
      })
      .finally(() => {
        // Keep the "refreshing" lock briefly to absorb the DOM mutations we cause.
        setTimeout(() => {
          this.isRefreshing = false;
        }, this.autoRefreshDelayMs);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
